

// Email template editor - fetches HTML templates from /api/files/{id},
// displays them in a Quill editor with email-safe toolbar only (bold, italic,
// underline, ordered/bullet lists). Saves edits back to /api/files/{id} as HTML.
// Also supports uploading a locally-edited .html file by filename.
import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from "react";
import { Card, Spinner } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import "quill/dist/quill.snow.css";
import SearchableSelect from "@/components/SearchableSelect";
import { BrandButton as Button, Toast } from "@/components";
import useToast from "@/components/useToast";
import { callApi } from "@/utils/callApi";
import { useMsal } from "@azure/msal-react";

// Array of current template_ids 
const TEMPLATE_IDS = [
  { id: "EmailTemplate_None",                       label: "None (test)" },
  { id: "EmailTemplate_Identity_Email",             label: "Identity - Email" },
  { id: "EmailTemplate_Identity_2FAToken",          label: "Identity - 2FA Token" },
  { id: "EmailTemplate_Identity_ConfirmationLink",  label: "Identity - Confirmation Link" },
  { id: "EmailTemplate_Identity_PostConfirmation",  label: "Identity - Post Confirmation" },
  { id: "EmailTemplate_Identity_PasswordResetLink", label: "Identity - Password Reset" },
  { id: "EmailTemplate_Survey_Published",           label: "Survey - Published" },
  { id: "EmailTemplate_Survey_Reminder",            label: "Survey - Reminder" },
  { id: "EmailTemplate_Survey_FinalReminder",       label: "Survey - Final Reminder" },
  { id: "EmailTemplate_Survey_Completed",           label: "Survey - Completed" },

]

// An array of just the TEMPLATE_IDS 'labels' only for use in the 'add new template' feature - this allows admins to select a label and category for the new template from the existing options to ensure consistency in naming conventions. The actual template ID is generated automatically based on the selected label and category when adding a new template.
const TEMPLATE_LABEL_CATEGORY_OPTIONS = TEMPLATE_IDS.map(template => {
  const [_, category, label] = template.id.split(/\.|_/); // split by . and _ to extract category and label
  return { id: template.id, label: `${category} - ${label}` }; // e.g. "Identity - Password Reset Link"
});

// Function to add template Ids to the array of template ids - this is used in the 'add new template' feature to add new template names to the list of templates available for selection and editing. The new template ID is generated based on the provided name and label, following the existing naming convention. The new template is added to the TEMPLATE_IDS array, which is used to populate the template selector dropdown.
const handleAddTemplateId = (name, labelName) => {
  const newId = `EmailTemplate.${name}_${label}`;
  TEMPLATE_IDS.push({ id: newId, label: `${name} - ${label}` });
  TEMPLATE_LABEL_CATEGORY_OPTIONS.push({ label: `${label}` });
};

// Email-safe Quill toolbar only — no colours, fonts, sizes, images or code blocks
// as these are unreliable in email clients.
const EMAIL_TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["clean"],
];

const panelStyle = { background: "#fff8f0", border: "1px solid #f8d7a0", borderRadius: "6px" };

export default function EmailTemplatesPage() {
  const { instance, accounts } = useMsal();
  const { toast, showToast, hideToast } = useToast();
  const fileInputRef = useRef(null);

  const [selectedId, setSelectedId]         = useState(null);
  const [htmlContent, setHtmlContent]       = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [notUploaded, setNotUploaded]       = useState(false); 
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateLabel, setNewTemplateLabel] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);

  // Find & replace state (table editor only)
  const [searchText, setSearchText]   = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [searchIndex, setSearchIndex] = useState(-1);
  const [searchNotFound, setSearchNotFound] = useState(false);
  const [htmlEditorRevealed, setHtmlEditorRevealed] = useState(false);
  const textareaRef = useRef(null);

  // setTimeout(0) runs after React finishes painting, so the controlled
  // textarea is fully synced before we set the selection.
  const selectInTextarea = (idx, len) => {
    // setTimeout(0): queue selection AFTER React finishes syncing the controlled textarea value
    setTimeout(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(idx, idx + len);
      const proportionalTop = (idx / Math.max(ta.value.length, 1)) * ta.scrollHeight;
      ta.scrollTop = Math.max(0, proportionalTop - ta.clientHeight / 2);
    }, 0);
  };

  // useLayoutEffect fires after React commits — so our scrollTop write wins.
  // We use a mirror div instead of counting \n chars because email HTML is often
  // minified into very long lines, making newline-counting return 0 even when the
  // match is thousands of characters in.
  useLayoutEffect(() => {
    if (searchIndex === -1) return;
    const ta = textareaRef.current;
    if (!ta) return;

    // Build an off-screen mirror div with identical styling to the textarea.
    // Fill it with text up to the match, then measure its rendered height.
    const cs = window.getComputedStyle(ta);
    const mirror = document.createElement("div");
    Object.assign(mirror.style, {
      position: "absolute", top: "-9999px", left: "-9999px",
      width: cs.width,
      fontFamily: cs.fontFamily,
      fontSize: cs.fontSize,
      lineHeight: cs.lineHeight,
      padding: cs.padding,
      border: cs.border,
      boxSizing: cs.boxSizing,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      overflowWrap: "break-word",
      visibility: "hidden",
    });
    mirror.textContent = ta.value.substring(0, searchIndex);
    document.body.appendChild(mirror);
    const pixelsAbove = mirror.scrollHeight;
    document.body.removeChild(mirror);

    // Scroll so the match appears roughly 3 "lines" from the top
    const lineHeight = parseFloat(cs.lineHeight) || 19.5;
    ta.scrollTop = Math.max(0, pixelsAbove - lineHeight * 3);
  }, [searchIndex]);

  const handleMatchFound = (idx) => {
    setSearchNotFound(false);
    setHtmlEditorRevealed(true);
    setSearchIndex(idx);
    selectInTextarea(idx, searchText.length);
  };

  const handleFind = (fromIndex = 0) => {
    if (!searchText) return;
    const idx = htmlContent.indexOf(searchText, fromIndex);
    if (idx === -1) {
      const wrapped = htmlContent.indexOf(searchText, 0);
      if (wrapped === -1) { setSearchNotFound(true); setSearchIndex(-1); return; }
      handleMatchFound(wrapped);
    } else {
      handleMatchFound(idx);
    }
  };

  const handleFindNext = () => handleFind(searchIndex >= 0 ? searchIndex + 1 : 0);

  const handleReplace = () => {
    if (searchIndex === -1 || !searchText) return;
    const updated = htmlContent.slice(0, searchIndex) + replaceText + htmlContent.slice(searchIndex + searchText.length);
    setHtmlContent(updated);
    const next = updated.indexOf(searchText, searchIndex + replaceText.length);
    setSearchIndex(next);
    if (next !== -1) {
      setHtmlEditorRevealed(true);
      selectInTextarea(next, searchText.length);
    }
  };

  const handleReplaceAll = () => {
    if (!searchText) return;
    const escaped = searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const count = (htmlContent.match(new RegExp(escaped, "g")) || []).length;
    setHtmlContent(htmlContent.replaceAll(searchText, replaceText));
    setSearchIndex(-1);
    showToast({ variant: "success", message: `Replaced ${count} occurrence${count !== 1 ? "s" : ""}` });
  };

  // ── Load ──────────────────────────────────────────────────────────────────
  const fetchTemplate = useCallback(async (templateId) => {
    setLoadingTemplate(true);
    setNotUploaded(false);
    setHtmlContent("");
    setHtmlEditorRevealed(false);
    setSearchIndex(-1);
    try {
      const html = await callApi(
        `/api/files/${templateId}`,
        { method: "GET" },
        instance,
        accounts
      );
      setHtmlContent(typeof html === "string" ? html.replace(/\r\n/g, "\n").replace(/\r/g, "\n") : "");
    } catch {
      // Any failure (404, 500, missing blob) means not uploaded yet.
      setNotUploaded(true);
    } finally {
      setLoadingTemplate(false);
    }
  }, [instance, accounts]);

  const handleSelectTemplate = (id) => {
    setSelectedId(id);
    if (id) fetchTemplate(id);
  };

  const handleSelectLabelCategory = (id) => {
    setNewTemplateLabel(id);
  };

  // ── Save from editor ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const blob = new Blob([htmlContent], { type: "text/html" });
      const file = new File([blob], `${selectedId}.html`, { type: "text/html" });
      const formData = new FormData();
      formData.append("file", file);
      await callApi(`/api/files/${selectedId}`, { method: "PUT", body: formData }, instance, accounts);
      showToast({ variant: "success", message: "Template saved successfully" });
      setNotUploaded(false);
    } catch (err) {
      showToast({ variant: "error", message: `Failed to save: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewTemplateName = (name, label) => {
    if (!name || !label) {
      showToast({ variant: "error", message: "Please provide both a template name and label." });
      return;
    }
    const id = `EmailTemplate.${name.replace(/\s+/g, "")}`;
    if (TEMPLATE_IDS.some(template => template.id === id)) {
      showToast({ variant: "error", message: "A template with this name already exists." });
      return;
    }
    // Create a new template name with the correct formatting.  EmailTemplate{TemplateIdCategory}_{TemplateLabel}  (e.g. EmailTemplate.Identity_PasswordResetLink)
    const newTemplate = { id: `EmailTemplate.${id}_${label}`, label };
    setNewTemplateName(id);
    setNewTemplateLabel(label);
    TEMPLATE_IDS.push(newTemplate);
    setSelectedId(newTemplate.id);
    setHtmlContent("");
    setNotUploaded(true);
    showToast({ variant: "success", message: `New template "${label}" added. It has not been saved to the server yet as to add a new template you need content, so add your content and click Save.` });
  }

  // ── Download current editor content as .html ──────────────────────────────
  const handleDownload = () => {
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `${selectedId}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Upload a locally-edited .html file ────────────────────────────────────
  // The filename (minus .html) is used as the template ID — do not rename the file.
  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    fileInputRef.current.value = "";  // reset so same file can be re-picked
    if (!file) return;

    const derivedId       = file.name.replace(/\.html$/i, "");
    const matchedTemplate = TEMPLATE_IDS.find(template => template.id === derivedId);
    if (!matchedTemplate) {
      showToast({
        variant: "error",
        message: `"${file.name}" does not match any known template ID. Do not rename the file.`
      });
      return;
    }

    const text = await file.text();
    setSelectedId(derivedId);
    setHtmlContent(text);
    setHtmlEditorRevealed(false);
    setSearchIndex(-1);
    setNotUploaded(false);

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await callApi(`/api/files/${derivedId}`, { method: "PUT", body: formData }, instance, accounts);
      showToast({ variant: "success", message: `"${matchedTemplate.label}" uploaded successfully` });
    } catch (err) {
      showToast({ variant: "error", message: `Upload failed: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const selectedTemplate = TEMPLATE_IDS.find(template => template.id === selectedId);

  return (
    <>
      <div className="container my-4">
        <h1 style={{ color: "#F16D09", marginBottom: "0.5rem" }}>Email Templates</h1>
        <p className="text-muted mb-4">
          Select a template to load it for editing. Use the editor for simple changes,
          or download the HTML file to edit it in a dedicated HTML editor and upload it back.
        </p>

        <div className="row">
          {/* ── Left: selector ───────────────────────────────────────────── */}
          <div className="col-md-4 mb-4">
            <Card className="p-3">
              <h5 style={{ color: "#F16D09", marginBottom: "1rem", fontSize: "var(--font-heading-sm)" }}>Templates</h5>
              <SearchableSelect
                options={TEMPLATE_IDS}
                value={selectedId}
                getLabel={(template) => template.label}
                getValue={(template) => template.id}
                onChange={handleSelectTemplate}
                placeholder="Search templates..."
              />
              {selectedId && (
                <div className="mt-3">
                  <p className="d-block mb-1" style={{ fontSize: "var(--font-base)" }}>Template ID:</p>
                  <code style={{ fontSize: "var(--font-xs)", wordBreak: "break-all" }}>{selectedId}</code>
                </div>
              )}
            </Card>
          </div>

          {/* ── Right: instructions and upload ───────────────────────────── */}
          <div className="col-md-8">
            {!selectedId ? (
              <div className="d-flex align-items-center justify-content-center"
                   style={{ minHeight: "200px", color: "#999" }}>
                <p>Select a template from the left to view and edit it.</p>
              </div>
            ) : loadingTemplate ? (
              <div className="d-flex align-items-center gap-2 p-4">
                <Spinner animation="border" size="sm" />
                <span>Loading template...</span>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {notUploaded && (
                  <div className="p-3" style={{ ...panelStyle, fontSize: "var(--font-heading-sm)", fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                    <p>This template has not been uploaded to the server yet.</p>
                    <p style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif", fontSize: "var(--font-base)" }}>
                      Use the editor below to create and save the template content, or if you have an existing HTML file, upload it using the button in the second panel.
                      Use one of the two panels below to create it.
                    </p>
                  </div>
                )}

                <Card className="p-3 mb-lg-3">
                  <h6 style={{ color: "#F16D09", marginBottom: "0.25rem", fontSize: "var(--font-heading-sm)" }}>
                    Option 1 — If you know HTML, Upload an HTML file
                  </h6>
                  <p className="text-muted mb-3" style={{ fontSize: "var(--font-base)" }}>
                    If you know HTML and want to edit the file in your own editor, download
                    it using the button in the editor panel below, make your changes, then
                    upload it here. The filename must not be changed — it must remain{" "}
                    <code>{selectedId}.html</code> so it uploads to the correct template.
                  </p>
                  <div>
                    <Button
                      variant="outline-secondary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={saving}
                    >
                      Upload HTML file
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".html"
                      style={{ display: "none" }}
                      onChange={handleUploadFile}
                    />
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>

        {selectedId && !loadingTemplate && (
          <div className="d-flex flex-column gap-3 mt-3">
            <Card className="p-3">
              <h6 style={{ color: "#F16D09", marginBottom: "0.25rem", fontSize: "var(--font-heading-sm)" }}>
                Option 2 — If you don't know HTML, edit the template here.
              </h6>

              {htmlContent && /<table/i.test(htmlContent) ? (
                <>
                <div className="d-flex flex-row mb-2 p-2" style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "4px" }}>
                <div className="col-9">
                  <p className="text-muted base-text mb-2">
                    <span className="semibold-text">{selectedTemplate?.label}</span> — this template contains a table.
                    Use the <em>Find</em> or <em>Find & Replace</em> bar to locate and edit text without
                    needing to read all the HTML. The live preview shows the result instantly.
                    If something breaks, just click <em>Reload from server</em>.
                  </p>
                  </div>

                  <div className="col-3 d-flex gap-2 mb-2 align-items-center justify-content-center">
                  <Button variant="outline-secondary" onClick={() => setShowInstructions(prev => !prev)} className="mb-2 p-2" style={{ fontSize: "var(--font-base)", }}>
                    {showInstructions ? "Hide detailed instructions" : "Show detailed instructions"}
                  </Button>
                  </div>
                  </div>
                  <div className="mb-2 p-2" style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "4px" }}>
                    <div className="row g-2 align-items-start mb-2">
                      <div className="col-12 col-lg-5">
                        <input
                          type="text"
                          value={searchText}
                          onChange={e => { setSearchText(e.target.value); setSearchIndex(-1); setHtmlEditorRevealed(false); setSearchNotFound(false); }}
                          onKeyDown={e => e.key === "Enter" && handleFind(0)}
                          placeholder="Find text in template…"
                          className="form-control form-control-sm"
                          style={{ borderColor: searchNotFound ? "red" : undefined }}
                        />
                      </div>
                      <div className="d-none d-lg-block col-lg-2" />
                      <div className="col-12 col-lg-5">
                        <input
                          type="text"
                          value={replaceText}
                          onChange={e => setReplaceText(e.target.value)}
                          placeholder="Replace with…"
                          className="form-control form-control-sm"
                        />
                      </div>
                    </div>

                    <div className="row g-2 align-items-start">
                      <div className="col-12 col-lg-5 d-flex gap-2 align-items-start flex-wrap">
                        <Button variant="outline-secondary" onClick={() => handleFind(0)} disabled={!searchText} style={{ fontSize: "var(--font-sm)", padding: "2px 10px" }}>
                          Find
                        </Button>
                        <Button variant="outline-secondary" onClick={handleFindNext} disabled={!searchText || searchIndex === -1} style={{ fontSize: "var(--font-sm)", padding: "2px 10px" }}>
                          Find next
                        </Button>
                        {searchNotFound && <span style={{ color: "red", fontSize: "var(--font-sm)" }}>Not found</span>}
                      </div>
                      <div className="d-none d-lg-block col-lg-2" />
                      <div className="col-12 col-lg-5 d-flex gap-2 align-items-start flex-wrap">
                        <Button variant="outline-secondary" onClick={handleReplace} disabled={!searchText || searchIndex === -1} style={{ fontSize: "var(--font-sm)", padding: "2px 10px" }}>
                          Replace
                        </Button>
                        <Button variant="outline-secondary" onClick={handleReplaceAll} disabled={!searchText} style={{ fontSize: "var(--font-sm)", padding: "2px 10px" }}>
                          Replace all
                        </Button>
                      </div>
                    </div>
                  </div>

                  {showInstructions && (
                    <div className="mb-2 p-2 invisible-toggle" style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "4px" }}>
                      <ol className="mb-0" style={{ fontSize: "var(--font-base)" }}>
                        <li>Paste the exact text you want to change by copying & pasting it from the right hand preview. <em>Find</em> highlights it so if you then scroll down through the text showing on the left of the preview then you will find your match (usually towards the bottom).</li>
                        <li>You can then edit the text you wanted to edit by typing over the highlighted text in the textarea on the left, or by using the <em>Replace</em> button to replace it with the text in the "Replace with" box.</li>
                        <li>Either way, the preview on the right updates as you type so if it doesn't do what you expect, you can correct it, or start again by clicking 'Reload from server'. </li>
                        <li>When you are sure you're finished you can click 'Save Template' to save your changes to the server, or leave the page and nothing will change.</li>
                      </ol>
                    </div>
                  )}

                  <div className="d-flex gap-2 mb-3" style={{ height: "40rem", marginTop: "0.5rem" }}>
                    <div style={{ flex: 1, position: "relative" }}>
                      {!htmlEditorRevealed && (
                        <div className="d-flex align-items-center justify-content-center"
                             style={{ position: "absolute", inset: 0, background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "4px", color: "#999", fontSize: "var(--font-sm)", textAlign: "center", padding: "1rem", zIndex: 1, pointerEvents: "none" }}>
                          <span>Type in the search bar above to find<br/>and edit text in the HTML</span>
                        </div>
                      )}
                      <textarea
                        ref={textareaRef}
                        value={htmlContent}
                        onChange={e => { setHtmlContent(e.target.value); setSearchIndex(-1); setHtmlEditorRevealed(true); }}
                        style={{
                          width: "100%",
                          height: "100%",
                          fontFamily: "monospace",
                          fontSize: "13px",
                          padding: "0.5rem",
                          border: "1px solid #ced4da",
                          borderRadius: "4px",
                          resize: "none",
                          lineHeight: 1.5,
                        }}
                        spellCheck={false}
                      />
                    </div>
                    <iframe
                      srcDoc={htmlContent}
                      sandbox="allow-same-origin"
                      title="Template preview"
                      style={{
                        flex: 1,
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        background: "#fff",
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <p className="text-muted mb-3" style={{ fontSize: "var(--font-base)" }}>
                    <strong>{selectedTemplate?.label}</strong> — use the editor below for
                    email-safe formatting (bold, italic, underline, lists).
                    Click <em>Save Template</em> when done.
                  </p>
                  <div className="mb-3">
                    <ReactQuill
                      theme="snow"
                      value={htmlContent}
                      onChange={setHtmlContent}
                      modules={{ toolbar: EMAIL_TOOLBAR }}
                      placeholder="Template HTML content..."
                    />
                    <style>{`.ql-editor { min-height: 300px; font-size: 15px; line-height: 1.6; }`}</style>
                  </div>
                </>
              )}

              <div className="d-flex gap-2 flex-wrap">
                <Button onClick={handleSave} disabled={saving}>
                  {saving
                    ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</>
                    : "Save Template"}
                </Button>
                <Button variant="outline-secondary" onClick={handleDownload} disabled={saving || notUploaded || !htmlContent || htmlContent === "<p><br></p>"}>
                  Download HTML
                </Button>
                <Button variant="outline-secondary" onClick={() => fetchTemplate(selectedId)} disabled={saving}>
                  Reload from server
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>

      {toast.show && (
        <Toast message={toast.message} variant={toast.variant} onClose={hideToast} />
      )}
    </>
  );
}
